import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { SERVER_BASE_URL } from '../../../environments/environment';
import { MatDialog } from '@angular/material/dialog';
import {
  StandardCheckoutResponse,
  UserRegistrationDto,
} from '../user-service/user.service';
import { VippsRequestParams, VippsResponse } from '../vipps/vipps-response.dto';
import { Observable } from 'rxjs/internal/Observable';
import { Currency } from '@app/model/currency';
import { LoanOptionVM } from '@app/route/shared/LoanOptionVM';
import { LoanOption } from '@app/route/shared/LoanOption';
import {
  KalpAnswersNO,
  KalpInfoReason,
  KalpQuestionType,
} from '../leq/leq.service';

@Injectable({
  providedIn: 'root',
})
export class NoUserService {
  private readonly httpReqOptions = { withCredentials: true };

  constructor(private http: HttpClient, private dialogService: MatDialog) {}

  initAuth(nin: string, callbackUrl: string): Promise<AuthUrlResponse> {
    return this.http
      .post<AuthUrlResponse>(
        `${SERVER_BASE_URL}/user/no/noBankID/initAuth/`,
        {
          nin,
          callbackUrl,
        },
        {
          withCredentials: true,
        }
      )
      .toPromise();
  }

  authorize(): Promise<StandardCheckoutResponse> {
    return this.http
      .post<StandardCheckoutResponse>(
        `${SERVER_BASE_URL}/user/no/noBankID/authentication/`,
        {
          withCredentials: true,
        }
      )
      .toPromise();
  }

  initSign(callbackUrl: string): Promise<AuthUrlResponse> {
    return this.http
      .post<AuthUrlResponse>(
        `${SERVER_BASE_URL}/user/no/noBankID/initSign/`,
        callbackUrl,
        {
          withCredentials: true,
        }
      )
      .toPromise();
  }

  sign(): Promise<StandardCheckoutResponse> {
    return this.http
      .post<StandardCheckoutResponse>(
        `${SERVER_BASE_URL}/user/no/noBankID/signature/`,
        {
          withCredentials: true,
        }
      )
      .toPromise();
  }

  registerEmail(email: string): Promise<StandardCheckoutResponse> {
    let emailWrapper: EmailWrapper = {
      email: email,
    };

    return this.http
      .post<StandardCheckoutResponse>(
        `${SERVER_BASE_URL}/user/no/registerEmail/`,
        emailWrapper,
        {
          withCredentials: true,
        }
      )
      .toPromise();
  }

  getLatestAnnualNetIncome(): Promise<IncomeCheckResponse> {
    return this.http
      .get<IncomeCheckResponse>(
        `${SERVER_BASE_URL}/user/no/getLatestAnnualNetIncome/`,
        {
          withCredentials: true,
        }
      )
      .toPromise();
  }

  getActiveUserInfo(): Promise<UserRegistrationDto> {
    return this.http
      .get<UserRegistrationDto>(
        `${SERVER_BASE_URL}/user/no/getActiveUserInfo/`,
        this.httpReqOptions
      )
      .toPromise();
  }

  getDataConsent(): Promise<boolean> {
    return this.http
      .get<boolean>(`${SERVER_BASE_URL}/user/no/needDebtRegisterApproval/`, {
        withCredentials: true,
      })
      .toPromise();
  }

  applyForLoan(
    userInputMonthlyNetIncome: number
  ): Promise<StandardCheckoutResponse> {
    let userInputMonthlyNetIncomeWrapper: UserInputMonthlyNetIncomeWrapper = {
      userInputMonthlyNetIncome: userInputMonthlyNetIncome,
    };

    return this.http
      .post<StandardCheckoutResponse>(
        `${SERVER_BASE_URL}/user/no/applyForLoan/`,
        userInputMonthlyNetIncomeWrapper,
        {
          withCredentials: true,
        }
      )
      .toPromise();
  }

  selectInstallmentPlan(loanOfferId: string): Promise<String> {
    const loanOffer = {
      id: loanOfferId,
    };

    return this.http
      .post<string>(
        `${SERVER_BASE_URL}/user/no/selectInstallmentPlan/`,
        loanOffer,
        {
          withCredentials: true,
        }
      )
      .toPromise();
  }

  vippsDeposit(param: VippsRequestParams): Promise<VippsResponse> {
    return this.http
      .post<VippsResponse>(
        `${SERVER_BASE_URL}/user/no/vipps/initiatePayment/`,
        {
          phoneNumber: param.phoneNumber,
          isMobile: param.isMobile,
          fallbackUrl: param.fallbackUrl,
          resumeTokenId: param.resumeTokenId,
        },
        { withCredentials: true }
      )
      .toPromise();
  }

  vippsGetStatus(): Observable<VippsResponse> {
    return this.http.post<VippsResponse>(
      `${SERVER_BASE_URL}/user/no/vipps/checkStatusAndSettlePayment/`,
      {
        withCredentials: true,
      }
    );
  }

  vippsCancel(vippsId: string): Observable<StandardCheckoutResponse> {
    let params = new HttpParams();
    params = params.append('vippsId', vippsId);

    return this.http.post<StandardCheckoutResponse>(
      `${SERVER_BASE_URL}/user/no/vipps/cancelPayment/`,
      {
        withCredentials: true,
        params: params,
      }
    );
  }

  openVipps(url: string): Promise<void> {
    return new Promise((resolve) => {
      setTimeout(() => {
        window.location.assign(url);
        resolve();
      }, 200);
    });
  }

  commit(): Promise<StandardCheckoutResponse> {
    return this.http
      .post<StandardCheckoutResponse>(`${SERVER_BASE_URL}/user/no/commit`, {
        withCredentials: true,
      })
      .toPromise();
  }

  getDebtRegisterInformation(): Promise<DebtRegisterReport> {
    return this.http
      .get<DebtRegisterReport>(
        `${SERVER_BASE_URL}/user/no/getDebtRegisterInformation`,
        {
          withCredentials: true,
        }
      )
      .toPromise();
  }

  getKalpInfo(reason: KalpInfoReason): Observable<KalpAnswersNO> {
    let params = new HttpParams();
    params = params.append('reason', reason);

    return this.http.get<KalpAnswersNO>(
      `${SERVER_BASE_URL}/user/no/kalp/getInfo/`,
      {
        withCredentials: true,
        params: params,
      }
    );
  }

  getKalpQuestions(): Promise<KalpQuestionType[]> {
    return this.http
      .get<KalpQuestionType[]>(`${SERVER_BASE_URL}/user/no/kalp/questions/`, {
        withCredentials: true,
      })
      .toPromise();
  }

  postKalpAnswers(
    kalpFormAnswers: KalpAnswersNO
  ): Promise<StandardCheckoutResponse> {
    return this.http
      .post<StandardCheckoutResponse>(
        `${SERVER_BASE_URL}/user/no/kalp/answers/`,
        kalpFormAnswers,
        this.httpReqOptions
      )
      .toPromise();
  }

  getScriveSignActive(): Promise<boolean> {
    return this.http
      .get<boolean>(`${SERVER_BASE_URL}/user/no/scrive/sign/active/`, {
        withCredentials: true,
      })
      .toPromise();
  }

  buildLoanTermsString(
    selectedLoanOption: LoanOptionVM,
    totalCheckoutValue: Currency
  ): string {
    const {
      effectiveInterest,
      nominalInterest,
      initialFee,
      monthlyFee,
      numberOfMonths,
      totalAmount,
    } = selectedLoanOption;

    return `Valgt kredittbeløp kr ${totalCheckoutValue.amount.toLocaleString(
      'no'
    )}, delbetaling ${numberOfMonths} måneder. Total sum av betalinger er kr ${totalAmount.toLocaleString(
      'no'
    )}
    og den effektive renten er ${this.getFormattedInterest(
      effectiveInterest
    )}%. Variabel årlig rente ${this.getFormattedInterest(
      nominalInterest
    )}%, etableringsgebyr kr ${initialFee.toLocaleString(
      'no'
    )}, administrasjonsgebyr kr ${monthlyFee.toLocaleString(
      'no'
    )}/mån og månedlig beløp kr ${selectedLoanOption.monthlyCost.toLocaleString(
      'no'
    )}/mån total sum av betalinger kr ${(
      selectedLoanOption.monthlyCost * selectedLoanOption.numberOfMonths
    ).toLocaleString('no')}.`;
  }

  zeroInterestString(loanOption: LoanOption): string {
    return loanOption.nominalInterest === 0 ? '0% renter' : '';
  }

  buildLoanOptionTitle(loanOption: LoanOption): string {
    return `kr ${loanOption.monthlyCost.toLocaleString('no')}/mnd i ${
      loanOption.numberOfMonths
    } måneder`;
  }

  getBulletPoints(item: LoanOption): string[] {
    return [`Årlig rente ${this.getFormattedInterest(item.nominalInterest)}%`];
  }

  getFormattedInterest(interest: number): string {
    return (interest * 100).toFixed(2);
  }

  transformDtosTOVMs(
    loanOptions: LoanOption[],
    toggleModal: () => void
  ): LoanOptionVM[] {
    return loanOptions.map((loanOption) =>
      this.transformDtoTOVM(loanOption, toggleModal)
    );
  }

  transformDtoTOVM(
    loanOption: LoanOption,
    toggleModal: () => void
  ): LoanOptionVM {
    return {
      badgeText: this.zeroInterestString(loanOption),
      bulletPoints: this.getBulletPoints(loanOption),
      color: '#00ac84',
      link: { text: 'Les villkår', onClick: toggleModal },
      monthlyCost: loanOption.monthlyCost,
      nominalInterest: loanOption.nominalInterest,
      numberOfMonths: loanOption.numberOfMonths,
      radioButtonColor: '#db305e',
      title: this.buildLoanOptionTitle(loanOption),
      termId: loanOption.termId,
      effectiveInterest: loanOption.effectiveInterest,
      initialFee: loanOption.initialFee,
      monthlyFee: loanOption.monthlyFee,
      totalAmount: loanOption.totalAmount,
      loanTermsURL: loanOption.loanTermsURL,
      secciURL: loanOption.secciURL,
    };
  }
}

export class EmailWrapper {
  email: string;
}

export class UserInputMonthlyNetIncomeWrapper {
  userInputMonthlyNetIncome: number;
}

export class AuthUrlResponse {
  accessUrl: string;
  id: string;
}

export class IncomeCheckResponse {
  latestAnnualNetIncome: Currency;
}

export interface DebtRegisterReport {
  debtRegisterInformation: DebtRegisterInformation[];
  totalDebt: number;
  totalMonthlyCharges: number;
}

export interface DebtRegisterInformation {
  financialInstitutionName: string;
  interestBearingBalance: number;
  balance: number;
  monthlyInstallmentCharges: number;
  totalBalance: number;
  loanType: string;
}
